import { ITimeProvider } from "../hexagon/adapters/driven/providers/provider";
import { useAppDispatch, useAppSelector } from "./redux";
import {
  removeStats,
  removeStatsHR,
  setStats,
  setStatsHR,
} from "../redux/Dashboard/dashboard.slice";
import { useEffect, useRef, useState } from "react";
import { STATS_FRESHNESS_MINUTES } from "../constants/stats";

type IStatsMap = { [key: string]: ICDLLatestVital };

export const useDashboardStats = ({
  patientController,
  timeProvider,
  useDailyVitals = false,
}: {
  patientController: Pick<IPatientController, "getPatientsLatestVitals">;
  timeProvider: ITimeProvider;
  useDailyVitals?: boolean;
}) => {
  const controllersRef = useRef<AbortController[]>([]);
  const dispatch = useAppDispatch();
  const [dailyStats, setDailyStats] = useState<IStatsMap>({});
  const [dailyStatsHR, setDailyStatsHR] = useState<IStatsMap>({});

  const { stats, statsHR, statsTimeout } = useAppSelector((state) => ({
    stats: state.dashboard.stats,
    statsHR: state.dashboard.statsHR,
    statsTimeout: state.dashboard.statsTimeout,
  }));

  const statsRef = useRef(stats);
  statsRef.current = stats;
  const statsHRRef = useRef(statsHR);
  statsHRRef.current = statsHR;
  const dailyStatsRef = useRef(dailyStats);
  dailyStatsRef.current = dailyStats;
  const dailyStatsHRRef = useRef(dailyStatsHR);
  dailyStatsHRRef.current = dailyStatsHR;
  const statsTimeoutRef = useRef(statsTimeout);
  statsTimeoutRef.current = statsTimeout;

  const inFlightRRRef = useRef<Set<number>>(new Set());
  const inFlightHRRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    return () => {
      controllersRef.current.forEach((controller) => {
        try {
          controller.abort();
        } catch {
          // Ignore abort errors - controller might already be aborted
        }
      });
      controllersRef.current = [];
    };
  }, []);

  const isStatsFresh = () => {
    if (!statsTimeoutRef.current) return false;
    const cutoff = timeProvider
      .dateLibrary()
      .subtract(STATS_FRESHNESS_MINUTES, "minutes")
      .unix();
    return statsTimeoutRef.current > cutoff;
  };

  const isMissingFrom = (cache: IStatsMap) => (patientId: number) =>
    !Object.hasOwn(cache, patientId) || cache[patientId].avg === null;

  const fetchLatestVitals = async ({
    metric,
    granularity,
    patientIds,
    onChunk,
  }: {
    metric: ICDLVitalsMetric;
    granularity: ICDLVitalsGranularity;
    patientIds: number[];
    onChunk: (chunkVitals: IStatsMap) => void;
  }) => {
    const inFlight =
      metric === "rr" ? inFlightRRRef.current : inFlightHRRef.current;
    const ids = patientIds.filter((id) => !inFlight.has(id));
    if (ids.length === 0) return;

    ids.forEach((id) => inFlight.add(id));
    const localController = new AbortController();
    controllersRef.current.push(localController);

    try {
      await patientController.getPatientsLatestVitals({
        patientIds: ids,
        metric,
        granularity,
        signal: localController.signal,
        onChunk,
      });
    } finally {
      const controllerIndex = controllersRef.current.indexOf(localController);
      if (controllerIndex > -1) {
        controllersRef.current.splice(controllerIndex, 1);
      }
      ids.forEach((id) => inFlight.delete(id));
    }
  };

  const getHourlyStats = async (
    metric: ICDLVitalsMetric,
    patientIds: number[]
  ) => {
    const cache = metric === "rr" ? statsRef.current : statsHRRef.current;
    const setAction = metric === "rr" ? setStats : setStatsHR;

    await fetchLatestVitals({
      metric,
      granularity: "hourly",
      patientIds: isStatsFresh()
        ? patientIds.filter(isMissingFrom(cache))
        : patientIds,
      onChunk: (chunkVitals) =>
        dispatch(
          setAction({
            value: chunkVitals,
            timeout: timeProvider.dateLibrary().unix(),
          })
        ),
    });
  };

  const getDailyStats = async (
    metric: ICDLVitalsMetric,
    patientIds: number[]
  ) => {
    const cache =
      metric === "rr" ? dailyStatsRef.current : dailyStatsHRRef.current;
    const setDaily = metric === "rr" ? setDailyStats : setDailyStatsHR;

    await fetchLatestVitals({
      metric,
      granularity: "daily",
      patientIds: patientIds.filter(isMissingFrom(cache)),
      onChunk: (chunkVitals) =>
        setDaily((prev) => ({
          ...prev,
          ...chunkVitals,
        })),
    });
  };

  const getStatsData = async (patientIds: number[]) => {
    if (useDailyVitals) {
      await getDailyStats("rr", patientIds);
      return;
    }

    await getHourlyStats("rr", patientIds);
  };

  const getStatsDataHR = async (patientIds: number[]) => {
    if (useDailyVitals) {
      await getDailyStats("hr", patientIds);
      return;
    }

    await getHourlyStats("hr", patientIds);
  };

  const removeStatsData = (residentIds?: number[]) => {
    dispatch(removeStats(residentIds));
  };

  const removeStatsDataHR = (residentIds?: number[]) => {
    dispatch(removeStatsHR(residentIds));
  };

  return {
    stats: useDailyVitals ? dailyStats : stats,
    getStatsData,
    removeStatsData,
    statsHR: useDailyVitals ? dailyStatsHR : statsHR,
    getStatsDataHR,
    removeStatsDataHR,
  };
};
